import { useState, useCallback } from 'react';
import { WifiOff, RefreshCw, Check, X } from 'lucide-react';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import styles from './OfflineBanner.module.css';
import type { OfflineBannerProps } from './OfflineBanner.types';

export function OfflineBanner({ className, showDetails = true }: OfflineBannerProps) {
  const { isOnline, checkConnection } = useOnlineStatus();
  const [isChecking, setIsChecking] = useState(false);

  const handleRetry = useCallback(async () => {
    setIsChecking(true);
    await checkConnection();
    setIsChecking(false);
  }, [checkConnection]);

  // Don't show banner when online
  if (isOnline) {
    return null;
  }

  const classNames = [styles.banner, className].filter(Boolean).join(' ');

  return (
    <div className={classNames} role="alert" aria-live="polite">
      <div className={styles.icon} aria-hidden="true">
        <WifiOff size={18} />
      </div>

      <div className={styles.content}>
        <p className={styles.title}>
          <span className={styles.pulse} aria-hidden="true" />
          You're offline
        </p>
        <p className={styles.description}>Some features may be unavailable until you reconnect</p>

        {showDetails && (
          <div className={styles.details}>
            <ul className={styles.detailsList}>
              <li className={`${styles.detailItem} ${styles.available}`}>
                <Check size={14} aria-hidden="true" />
                <span>View designs</span>
              </li>
              <li className={`${styles.detailItem} ${styles.available}`}>
                <Check size={14} aria-hidden="true" />
                <span>Edit canvas</span>
              </li>
              <li className={`${styles.detailItem} ${styles.available}`}>
                <Check size={14} aria-hidden="true" />
                <span>Cart items</span>
              </li>
              <li className={`${styles.detailItem} ${styles.unavailable}`}>
                <X size={14} aria-hidden="true" />
                <span>AI generation</span>
              </li>
              <li className={`${styles.detailItem} ${styles.unavailable}`}>
                <X size={14} aria-hidden="true" />
                <span>Checkout</span>
              </li>
              <li className={`${styles.detailItem} ${styles.unavailable}`}>
                <X size={14} aria-hidden="true" />
                <span>Cloud sync</span>
              </li>
            </ul>
          </div>
        )}
      </div>

      <button
        type="button"
        className={styles.retryButton}
        onClick={handleRetry}
        disabled={isChecking}
        aria-label="Check connection"
      >
        <RefreshCw size={14} className={isChecking ? styles.spinning : ''} aria-hidden="true" />
        {isChecking ? 'Checking...' : 'Retry'}
      </button>
    </div>
  );
}
